/**
 * Workspaces the user just hid (Only / toggle off).
 * Stale URL sync must not add them back. A different document URL, Cmd+P,
 * or a cold deep link clears the mark.
 */

const hiddenAtPathname = new Map<string, string>()

function normalizePathname(pathname: string): string {
  const trimmed = pathname.split('?')[0].replace(/\/+$/, '')
  if (!trimmed || trimmed === '') return '/'
  return trimmed.startsWith('/') ? trimmed : `/${trimmed}`
}

export function markWorkspacesUserHidden(
  ids: string[],
  pathname: string = typeof window !== 'undefined' ? window.location.pathname : '/',
): void {
  const at = normalizePathname(pathname)
  for (const id of ids) {
    if (id) hiddenAtPathname.set(id, at)
  }
}

export function clearUserHiddenWorkspace(id: string): void {
  hiddenAtPathname.delete(id)
}

export function clearUserHiddenWorkspaces(ids: string[]): void {
  for (const id of ids) hiddenAtPathname.delete(id)
}

export function isWorkspaceUserHidden(id: string): boolean {
  return hiddenAtPathname.has(id)
}

/** Pathname (no query) where the user hid this workspace, if still marked. */
export function userHiddenWorkspacePathname(id: string): string | null {
  return hiddenAtPathname.get(id) ?? null
}

/**
 * True when opening this exact pathname should not re-enable a workspace
 * the user hid while already on it (stale tab-to-URL / url-to-tab).
 * A different pathname is a new navigation and is not blocked.
 */
export function isWorkspaceAutoEnableBlocked(wsId: string, openingPathname: string): boolean {
  const hiddenAt = hiddenAtPathname.get(wsId)
  if (!hiddenAt) return false
  return hiddenAt === normalizePathname(openingPathname)
}

export function openingPathnameForDocument(contentId: string): string {
  const trimmed = contentId.replace(/^\/+/, '').split('?')[0]
  if (!trimmed) return '/'
  return `/${trimmed}`
}

/** Test-only reset. */
export function __resetUserHiddenWorkspacesForTests(): void {
  hiddenAtPathname.clear()
}
